import { MouseEvent, useEffect, useState } from "react";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import { useMap } from "@app/component/map/FeaturedMap";

export type ContinuousSelectionMode = "add" | "none" | "remove" | "toggle";

const useContinuousSelectionMode = (): ContinuousSelectionMode => {
  const [isAltPressed, setAltPressed] = useState<boolean>(false);
  const [isShiftPressed, setShiftPressed] = useState<boolean>(false);
  const [isMouseDown, setMouseDown, setMouseUp] = useBoolean(false);

  const { map } = useMap();
  useEffect(() => {
    const div = map.getDiv();
    if (!div) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Shift") {
        setShiftPressed(event.shiftKey);
      }
      if (event.key === "Alt") {
        setAltPressed(event.altKey);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Shift") {
        setShiftPressed(false);
      }
      if (event.key === "Alt") {
        setAltPressed(false);
      }
    };

    div.addEventListener("keydown", handleKeyDown);
    div.addEventListener("keyup", handleKeyUp);
    const mouseDownListener = map.data.addListener(
      "mousedown",
      (event: google.maps.Data.MouseEvent) => {
        // it's better to check shiftKey status again, because when doing first click on a map, map's div
        // may not be in focus yet, hence keydown event might not be caught yet and isShiftPressed
        // flag is still false
        if ((event.domEvent as unknown as MouseEvent).shiftKey) {
          setShiftPressed(true);
        }
        if ((event.domEvent as unknown as MouseEvent).altKey) {
          setAltPressed(true);
        }
        setMouseDown();
      }
    );
    const mouseUpListener = map.data.addListener("mouseup", setMouseUp);

    return () => {
      mouseUpListener.remove();
      mouseDownListener.remove();
      div.removeEventListener("keyup", handleKeyUp);
      div.removeEventListener("keydown", handleKeyDown);
    };
  }, [map, setMouseUp, setMouseDown]);

  const isActive = (isShiftPressed || isAltPressed) && isMouseDown;

  useEffect(() => {
    // disable (when active) or enable dragging
    map.setOptions({ gestureHandling: isActive ? "none" : "greedy" });
  }, [map, isActive]);

  return isActive
    ? isAltPressed && isShiftPressed
      ? "toggle"
      : isAltPressed
      ? "remove"
      : "add"
    : "none";
};

export default useContinuousSelectionMode;
